import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'

// iOS Crash Recovery - Simplified and more robust
// Only handles critical chunk loading errors, max 2 attempts
const isProduction = import.meta.env.PROD;

if (isProduction) {
  const reloadCount = parseInt(sessionStorage.getItem('reloadCount') || '0');
  const MAX_RELOADS = 2;
  
  // Only handle critical chunk loading errors
  const handleCriticalError = (error: any) => {
    const errorMsg = error?.message || error?.reason?.message || String(error) || '';
    
    // Only reload for chunk loading errors - these indicate a new deployment
    const isChunkError = 
      errorMsg.includes('chunk') || 
      errorMsg.includes('Loading chunk') ||
      errorMsg.includes('Failed to fetch dynamically imported module') ||
      errorMsg.includes('Importing a module script failed');
    
    if (isChunkError && reloadCount < MAX_RELOADS) {
      console.warn(`[BuxBox] Chunk loading error detected (attempt ${reloadCount + 1}/${MAX_RELOADS})`);
      sessionStorage.setItem('reloadCount', String(reloadCount + 1));
      // Use replace to avoid building up browser history
      setTimeout(() => window.location.replace(window.location.pathname), 100);
    } else if (reloadCount >= MAX_RELOADS) {
      console.error('[BuxBox] Max reload attempts reached:', error);
      // Clear the counter so user can try again later
      sessionStorage.removeItem('reloadCount');
    }
    // For non-chunk errors, let ErrorBoundary handle them
  };

  window.addEventListener('error', (event) => handleCriticalError(event.error), { once: true });
  window.addEventListener('unhandledrejection', (event) => handleCriticalError(event.reason), { once: true });
  
  // Clear reload counter after successful app load (3 seconds is enough for React to mount)
  window.addEventListener('load', () => {
    setTimeout(() => {
      sessionStorage.removeItem('reloadCount');
    }, 3000);
  });
}

// Safari Detection - Add class to html for Safari-specific CSS overrides
// Safari has compositor issues with large backdrop-filter during fast scroll
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
  // iOS Safari and other iOS browsers (all use WebKit)
  (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream);

if (isSafari) {
  document.documentElement.classList.add('safari');
}

// iOS PWA URL Cleanup - Strip query params in standalone mode to prevent duplicate icons
const isStandalone = 
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true;

if (isStandalone && window.location.search) {
  // Preserve essential session data before cleanup
  const params = new URLSearchParams(window.location.search);
  const trackId = params.get('trackId');
  const audiobookId = params.get('audiobookId');
  
  // Store share params in sessionStorage for the app to handle
  if (trackId || audiobookId) {
    sessionStorage.setItem('pwa-share-params', JSON.stringify({
      trackId,
      audiobookId,
      path: window.location.pathname
    }));
  }
  
  // Clean the URL
  window.history.replaceState({}, '', window.location.pathname);
}

// Hide initial loader and render app
document.documentElement.classList.add('app-loaded');

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
